import { HttpErrorResponse } from "@angular/common/http";
import type {
  FeatureFlagDefinitionCreateRequest as GeneratedFeatureFlagDefinitionCreateRequest,
  FeatureFlagDefinitionDetail as GeneratedFeatureFlagDefinitionDetail,
  FeatureFlagDefinitionSummary as GeneratedFeatureFlagDefinitionSummary,
  FeatureFlagDefinitionUpdateRequest as GeneratedFeatureFlagDefinitionUpdateRequest,
  FeatureFlagRollout as GeneratedFeatureFlagRollout,
  FeatureFlagRolloutUpsertRequest as GeneratedFeatureFlagRolloutUpsertRequest,
} from "@app/generated/contracts";

export type FeatureFlagDefinitionSummary = GeneratedFeatureFlagDefinitionSummary;
export type FeatureFlagDefinitionDetail = GeneratedFeatureFlagDefinitionDetail;
export type FeatureFlagRollout = GeneratedFeatureFlagRollout;
export type FeatureFlagDefinitionCreateRequest = GeneratedFeatureFlagDefinitionCreateRequest;
export type FeatureFlagDefinitionUpdateRequest = GeneratedFeatureFlagDefinitionUpdateRequest;
export type FeatureFlagRolloutUpsertRequest = GeneratedFeatureFlagRolloutUpsertRequest;

export interface FeatureFlagDefinitionFormValues {
  flagCode: string;
  displayName: string;
  description: string;
  flagTypeCode: string;
  defaultEnabled: boolean;
  ownerCode: string;
}

export interface FeatureFlagRolloutFormValues {
  scopeTypeCode: string;
  scopeKey: string;
  audienceCode: string;
  environmentCode: string;
  enabledStateCode: string;
  precedenceRank: number | string | null;
  rollbackPostureCode: string;
  effectiveStartUtc: string | Date | null;
  effectiveEndUtc: string | Date | null;
}

export interface FeatureFlagAdminProblemDetails {
  type?: string;
  title?: string;
  detail?: string;
  status?: number;
  problemCode?: string;
  issueCodes?: string[];
  errors?: Record<string, string[] | string>;
}

export interface FeatureFlagAdminDisplayError {
  title: string;
  messages: string[];
  statusCode?: number;
  problemCode?: string;
  issueCodes: string[];
  correlationId?: string | null;
}

export function mapFeatureFlagAdminError(error: unknown): FeatureFlagAdminDisplayError {
  if (error instanceof HttpErrorResponse) {
    const problem = toProblemDetails(error.error);
    const messages = new Set<string>();

    const flattenedValidationMessages = flattenValidationMessages(problem.errors);
    flattenedValidationMessages.forEach((message) => messages.add(message));

    if (typeof problem.detail === "string" && problem.detail.trim().length > 0) {
      messages.add(problem.detail.trim());
    }

    if (messages.size === 0) {
      messages.add(getDefaultErrorMessage(error.status));
    }

    return {
      title: normalizeText(problem.title) || getDefaultErrorTitle(error.status),
      messages: [...messages],
      statusCode: error.status || problem.status,
      problemCode: normalizeText(problem.problemCode) || undefined,
      issueCodes: Array.isArray(problem.issueCodes)
        ? problem.issueCodes.filter((code): code is string => normalizeText(code).length > 0)
        : [],
      correlationId: error.headers?.get("X-Correlation-ID"),
    };
  }

  return {
    title: "Unexpected error",
    messages: ["The request could not be completed. Try again or contact support if the problem persists."],
    issueCodes: [],
  };
}

function toProblemDetails(errorBody: unknown): FeatureFlagAdminProblemDetails {
  if (!errorBody || typeof errorBody !== "object" || Array.isArray(errorBody)) {
    return {};
  }

  return errorBody as FeatureFlagAdminProblemDetails;
}

function flattenValidationMessages(errors: FeatureFlagAdminProblemDetails["errors"]): string[] {
  if (!errors || typeof errors !== "object") {
    return [];
  }

  return Object.values(errors).flatMap((entry) => {
    if (Array.isArray(entry)) {
      return entry
        .map((item) => normalizeText(item))
        .filter((item) => item.length > 0);
    }

    const normalized = normalizeText(entry);
    return normalized.length > 0 ? [normalized] : [];
  });
}

function getDefaultErrorTitle(statusCode: number): string {
  switch (statusCode) {
    case 400:
      return "Request could not be completed";
    case 401:
      return "Sign-in required";
    case 403:
      return "Access denied";
    case 404:
      return "Feature flag not found";
    case 409:
      return "Conflict detected.";
    default:
      return "Unexpected error";
  }
}

function getDefaultErrorMessage(statusCode: number): string {
  switch (statusCode) {
    case 400:
      return "Review the highlighted values and try again.";
    case 401:
      return "Your session is no longer authenticated. Sign in again and retry the action.";
    case 403:
      return "You do not have permission to perform this action.";
    case 404:
      return "The requested feature flag or rollout could not be found.";
    case 409:
      return "The requested change conflicts with the current lifecycle state.";
    default:
      return "The request could not be completed. Try again or contact support if the problem persists.";
  }
}

function normalizeText(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}
